import { useMemo, createElement } from "react";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import { ClearSelectionsFooter } from "@/shared/components/FilterComponents";
import { isPrebuiltWatchlist } from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { useHighlightPanelMenu } from "@/shared/hooks/useHighlightPanelMenu";
import { Portfolio } from "@/types/Portfolio";
import {
  groupWatchlistsByCategory,
  NEWSQUAWK_CATEGORIES,
  NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";

interface HeadlinesHighlightPanelOptions {
  setActions?: (actions: React.ReactElement[]) => void;
  pushAction?: (action: React.ReactElement) => void;
  persistKey?: string;
  /** Callback when panel opens - use to close other panels */
  onOpen?: () => void;
  closeSearch?: () => void;
  onSearchOpen?: (callback: () => void) => () => void;
}

// Helper to check if a portfolio is ticker-based (has tickers, no keywords)
const isTickerWatchlist = (p: Portfolio): boolean => {
  return p.tickers.length > 0 && (!p.keywords || p.keywords.length === 0);
};

// Helper to check if a portfolio is keyword-based (has keywords, no tickers)
const isKeywordWatchlist = (p: Portfolio): boolean => {
  return (p.keywords?.length ?? 0) > 0 && p.tickers.length === 0;
};

export const useHeadlinesHighlightPanel = (
  options: HeadlinesHighlightPanelOptions
) => {
  const { portfolios } = usePortfoliosCRUD();

  // Define highlight configurations
  const highlightConfig = useMemo(() => {
    // Separate user and prebuilt watchlists
    const userWatchlists = portfolios.filter((p) => !isPrebuiltWatchlist(p));
    const prebuiltWatchlists = portfolios.filter((p) => isPrebuiltWatchlist(p));

    // Split user watchlists by type
    const userTickerWatchlists = userWatchlists.filter(isTickerWatchlist);
    const userKeywordWatchlists = userWatchlists.filter(isKeywordWatchlist);
    // Mixed watchlists (both tickers and keywords) go in ticker section
    const userMixedWatchlists = userWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Split prebuilt watchlists by type
    const prebuiltTickerWatchlists = prebuiltWatchlists.filter(isTickerWatchlist);
    const prebuiltKeywordWatchlists = prebuiltWatchlists.filter(isKeywordWatchlist);
    const prebuiltMixedWatchlists = prebuiltWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Group prebuilt keyword watchlists by category
    const groupedKeywordWatchlists = groupWatchlistsByCategory(prebuiltKeywordWatchlists);

    // All ticker watchlist options (user + prebuilt)
    const allTickerWatchlists = [
      ...userTickerWatchlists,
      ...userMixedWatchlists,
      ...prebuiltTickerWatchlists,
      ...prebuiltMixedWatchlists,
    ];
    const tickerWatchlistOptions = allTickerWatchlists.map((portfolio) => ({
      id: String(portfolio.id),
      label: portfolio.name,
    }));

    // Create subsections for ticker watchlists
    const tickerSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    // Add user ticker watchlists subsection (always show to allow creating new)
    const userTickerIds = [...userTickerWatchlists, ...userMixedWatchlists].map((p) => String(p.id));
    tickerSubsections.push({
      id: "user-tickers",
      label: "Your Watchlists",
      filterIds: userTickerIds,
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=ticker" },
    });

    // Add Newsquawk ticker watchlists subsection
    const newsquawkTickerIds = [...prebuiltTickerWatchlists, ...prebuiltMixedWatchlists].map(
      (p) => String(p.id)
    );
    if (newsquawkTickerIds.length > 0) {
      tickerSubsections.push({
        id: "newsquawk-tickers",
        label: "Newsquawk Watchlists",
        filterIds: newsquawkTickerIds,
        defaultCollapsed: true,
      });
    }

    // Build keyword watchlist options with category headers for Newsquawk watchlists
    const keywordWatchlistOptions: { id: string; label: string; isHeader?: boolean }[] = [];

    // Add user keyword watchlists first
    for (const portfolio of userKeywordWatchlists) {
      keywordWatchlistOptions.push({
        id: String(portfolio.id),
        label: portfolio.name,
      });
    }

    // Add Newsquawk keyword watchlists grouped by category with headers
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        // Add category header as non-interactive label
        keywordWatchlistOptions.push({
          id: `header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`,
          label: categoryConfig.label,
          isHeader: true,
        });
        // Add watchlists in this category
        for (const portfolio of categoryWatchlists) {
          keywordWatchlistOptions.push({
            id: String(portfolio.id),
            label: portfolio.name,
          });
        }
      }
    }

    // Create subsections for keyword watchlists (just two: user and Newsquawk)
    const keywordSubsections: { id: string; label: string; filterIds: string[]; defaultCollapsed: boolean; createNewLink?: { url: string; label?: string } }[] = [];

    // Add user keywords as first subsection (always show to allow creating new)
    keywordSubsections.push({
      id: "user-keywords",
      label: "Your Watchlists",
      filterIds: userKeywordWatchlists.map((p) => String(p.id)),
      defaultCollapsed: false,
      createNewLink: { url: "/watchlists-filters?create=keyword" },
    });

    // Add all Newsquawk keyword watchlists in one subsection (with category headers inline)
    const allNewsquawkKeywordIds: string[] = [];
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        // Include the header ID
        allNewsquawkKeywordIds.push(`header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`);
        // Include watchlist IDs
        for (const portfolio of categoryWatchlists) {
          allNewsquawkKeywordIds.push(String(portfolio.id));
        }
      }
    }
    if (allNewsquawkKeywordIds.length > 0) {
      keywordSubsections.push({
        id: "newsquawk-keywords",
        label: "Newsquawk Watchlists",
        filterIds: allNewsquawkKeywordIds,
        defaultCollapsed: true,
      });
    }

    return [
      // Highlight sections - Ticker Watchlists
      {
        id: "ticker-watchlists-highlight",
        label: "Highlight by Ticker Watchlists",
        description: "Highlight by stock symbol watchlists",
        filterOptions: tickerWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: tickerSubsections.length > 0 ? tickerSubsections : undefined,
      },
      // Highlight sections - Keyword Watchlists
      {
        id: "keyword-watchlists-highlight",
        label: "Highlight by Keyword Watchlists",
        description: "Highlight by keyword watchlists",
        filterOptions: keywordWatchlistOptions,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        subsections: keywordSubsections.length > 0 ? keywordSubsections : undefined,
      },
    ] as FilterMenuConfig[];
  }, [portfolios]);

  // Create global footer component with just clear selections button
  const globalFooter = useMemo(
    () => createElement(ClearSelectionsFooter, {}),
    []
  );

  const highlightPanel = useHighlightPanelMenu(
    highlightConfig,
    options.pushAction,
    options.persistKey,
    globalFooter,
    options.onOpen,
    options.closeSearch,
    options.onSearchOpen
  );

  return highlightPanel;
};
